import React from 'react';
import OccupationCard from './OccupationCard';
import { Occupation } from '../../SalaryCalculator/types';
import { useCalculatorContext } from '../contexts';

/**
 * Context-driven version of OccupationCardGrid that gets all data from CalculatorContext
 * This component doesn't need any props and manages its own state through context
 */
const OccupationCardGrid: React.FC = () => {
  const {
    occupations = [],
    loadingOccupations = false,
    getCalculatorValue,
    updateCalculatorValue,
    triggerDataFetch,
    hasInitialFetch = false,
  } = useCalculatorContext();

  // Get selected occupation from context
  const selectedOccupationCard = getCalculatorValue('selectedOccupation');

  // Check if we have complete DCS selections
  const isDCSComplete =
    getCalculatorValue('degree') &&
    getCalculatorValue('category') &&
    getCalculatorValue('subject');

  // Determine state: initial load, no results after fetch, or needs CTA for update
  const isInitialState =
    !hasInitialFetch && occupations.length === 0 && !loadingOccupations;
  const needsCTAForUpdate =
    hasInitialFetch && occupations.length === 0 && !loadingOccupations;

  const handleCardSelect = (occupation: Occupation) => {
    // Toggle selection: if already selected, unset it, otherwise set it
    const newSelection =
      selectedOccupationCard === occupation ? null : occupation;
    updateCalculatorValue('selectedOccupation', newSelection);
  };

  if (loadingOccupations) {
    return (
      <div className='grid grid-cols-2 gap-4'>
        <div className='col-span-1 md:col-span-2 flex flex-col items-center justify-center bg-white rounded-lg shadow p-8'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4'></div>
          <p className='text-gray-600 font-body-16'>
            Loading occupation data...
          </p>
          <p className='text-gray-500 font-body-14 mt-2'>
            This may take a few moments
          </p>
        </div>
      </div>
    );
  }

  if (!occupations.length) {
    return (
      <div className='grid grid-cols-2 gap-4'>
        <div className='col-span-1 md:col-span-2 bg-white rounded-lg shadow p-6 text-center'>
          {isInitialState ? (
            <div>
              <div className='text-gray-500 font-body-16 mb-2'>
                {isDCSComplete
                  ? 'Click "Get My Results" to see salary data'
                  : 'Select your degree, category, and subject to get started'}
              </div>
            </div>
          ) : (
            <div>
              <div className='text-gray-500 font-body-16 mb-2'>
                No occupation data available
              </div>
              <div className='text-gray-400 font-body-14'>
                Try selecting a different subject or category to see salary
                comparisons.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-2 gap-4'>
      {occupations.map((occupation, index) => (
        <OccupationCard
          key={`${occupation.onetSocCode}-${index}`}
          index={index}
          occupation={occupation}
          isSelected={selectedOccupationCard === occupation}
          onSelect={handleCardSelect}
        />
      ))}
    </div>
  );
};

export default OccupationCardGrid;
